import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ErrorNotificationProvider } from '@/components/ErrorNotificationProvider'
import { TranslationProvider } from '@/contexts/TranslationContext'
import { TimezoneDisplayProvider } from '@/contexts/TimezoneDisplayContext'
import { PresenceProvider } from '@/contexts/PresenceContext'
import App from './app/router'
import './styles/globals.css'
import './i18n'

// Initialize theme system immediately (before React renders)
// This prevents FOUC (Flash of Unstyled Content)
const initializeTheme = () => {
    const storedTheme = localStorage.getItem('ui-storage')

    if (storedTheme) {
        try {
            const parsed = JSON.parse(storedTheme)
            const theme = parsed.state?.theme || 'light'

            const root = document.documentElement
            root.classList.remove('dark', 'enhanced-light', 'enhanced-dark')

            if (theme === 'system') {
                const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
                if (prefersDark) {
                    root.classList.add('dark')
                }
            } else if (theme === 'dark' || theme === 'enhanced-dark') {
                root.classList.add('dark')
            }

            if (theme === 'enhanced-light' || theme === 'enhanced-dark') {
                root.classList.add(theme)
                root.setAttribute('data-theme', theme)
            }

            // CSS automatically applies all variables
        } catch (e) {
            console.error('Error parsing theme, fallback to light:', e)
            // Fallback to light theme
            const root = document.documentElement
            root.classList.remove('dark', 'enhanced-light', 'enhanced-dark')
        }
    } else {
        // Default to light theme
        const root = document.documentElement
        root.classList.remove('dark', 'enhanced-light', 'enhanced-dark')
    }
}

// Apply theme before React renders to avoid FOUC
initializeTheme()

// Initialize MSW in development
// Temporarily disabled due to cookie parsing issues
// if (import.meta.env.DEV) {
//     import('./test/mocks/server')
// }

// Conditionally import ReactQueryDevtools only in development
const ReactQueryDevtools = import.meta.env.DEV
    ? React.lazy(() => import('@tanstack/react-query-devtools').then(d => ({ default: d.ReactQueryDevtools })))
    : null

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: (failureCount, error: any) => {
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    return false
                }
                return failureCount < 3
            },
        },
    },
})

// Disable StrictMode in development to prevent double-renders that cause lag
// StrictMode intentionally double-renders to help find bugs, but it makes hover feel slow
const AppWrapper = (
    <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
            <TranslationProvider>
                <TimezoneDisplayProvider>
                <PresenceProvider>
                    <BrowserRouter
                        future={{
                            v7_startTransition: true,
                            v7_relativeSplatPath: true
                        }}
                    >
                        <ErrorNotificationProvider>
                            <App />
                        </ErrorNotificationProvider>
                        <Toaster />
                        {ReactQueryDevtools && (
                            <React.Suspense fallback={null}>
                                <ReactQueryDevtools initialIsOpen={false} />
                            </React.Suspense>
                        )}
                    </BrowserRouter>
                </PresenceProvider>
                </TimezoneDisplayProvider>
            </TranslationProvider>
        </QueryClientProvider>
    </ErrorBoundary>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
    import.meta.env.PROD ? <React.StrictMode>{AppWrapper}</React.StrictMode> : AppWrapper
)
